import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  TableCellsIcon,
  CodeBracketIcon,
  InformationCircleIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../Common/LoadingSpinner";
import toast from "react-hot-toast";
import { isValidDate } from "../../utils/dateUtils";

const AssignmentEdit = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [activeQuestionType, setActiveQuestionType] =
    useState("multiple-choice");
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    courseId: "",
    type: "homework",
    totalPoints: 100,
    dueDate: "",
    submissionType: "both",
    allowedFileTypes: ["pdf", "doc", "docx"],
    maxFileSize: 10485760, // 10MB
    instructions: "",
    isPublished: false,
    allowLateSubmission: true,
    latePenalty: 10,
    rubric: [],
    quizSettings: {
      randomizeQuestions: false,
      timeLimit: null,
      maxAttempts: 1,
      autoGrade: true,
    },
    questions: [],
  });

  useEffect(() => {
    fetchInstructorCourses();
    fetchAssignment();
  }, [id]);

  const fetchInstructorCourses = async () => {
    try {
      const response = await axios.get(`/api/courses/instructor/${user._id}`);
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchAssignment = async () => {
    try {
      const response = await axios.get(`/api/assignments/${id}`);
      const assignment = response.data;

      // Check if assignment is overdue - redirect to details page if so
      const isOverdue =
        assignment?.dueDate && isValidDate(assignment.dueDate)
          ? new Date() > new Date(assignment.dueDate)
          : false;

      // if (isOverdue) {
      //     toast.error('Cannot edit overdue assignments');
      //     navigate(`/assignments/${id}`);
      //     return;
      // }

      // Convert dueDate to datetime-local format
      let dueDateValue = "";
      if (assignment.dueDate) {
        const date = new Date(assignment.dueDate);
        if (!isNaN(date.getTime())) {
          dueDateValue = date.toISOString().slice(0, 16);
        }
      }

      setFormData({
        title: assignment.title || "",
        description: assignment.description || "",
        courseId: assignment.course?._id || "",
        type: assignment.type || "homework",
        totalPoints: assignment.totalPoints || 100,
        dueDate: dueDateValue,
        submissionType: assignment.submissionType || "both",
        allowedFileTypes: assignment.allowedFileTypes || ["pdf", "doc", "docx"],
        maxFileSize: assignment.maxFileSize || 10485760,
        instructions: assignment.instructions || "",
        isPublished: assignment.isPublished || false,
        allowLateSubmission:
          assignment.allowLateSubmission !== undefined
            ? assignment.allowLateSubmission
            : true,
        latePenalty: assignment.latePenalty || 10,
        rubric: assignment.rubric || [],
        quizSettings: assignment.quizSettings || {
          randomizeQuestions: false,
          timeLimit: null,
          maxAttempts: 1,
          autoGrade: true,
        },
        questions: assignment.questions || [],
      });

      setExistingAttachments(assignment.attachments || []);
    } catch (error) {
      console.error("Error fetching assignment:", error);
      toast.error("Failed to load assignment");
      navigate("/assignments");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "dueDate") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? parseInt(value) || 0
            : value,
      }));
    }
  };

  const handleFileTypeChange = (fileType, checked) => {
    setFormData((prev) => ({
      ...prev,
      allowedFileTypes: checked
        ? [...prev.allowedFileTypes, fileType]
        : prev.allowedFileTypes.filter((type) => type !== fileType),
    }));
  };

  const handleQuizSettingChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      quizSettings: {
        ...prev.quizSettings,
        [field]: value,
      },
    }));
  };

  const handleQuestionTypeChange = (type) => {
    setActiveQuestionType(type);
  };

  const addQuestion = (type) => {
    const newQuestion = {
      questionText: "",
      type: type,
      points: 1,
      explanation: "",
      difficulty: "easy",
      tags: [],
    };

    if (type === "multiple-choice") {
      newQuestion.options = ["", "", "", ""];
      newQuestion.correctOption = 0;
    } else {
      newQuestion.expectedAnswer = "";
    }

    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const removeQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, [field]: value } : q
      ),
    }));
  };

  const updateQuestionOption = (questionIndex, optionIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === questionIndex) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      }),
    }));
  };

  const addQuestionOption = (questionIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex ? { ...q, options: [...q.options, ""] } : q
      ),
    }));
  };

  const removeQuestionOption = (questionIndex, optionIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === questionIndex && q.options.length > 2) {
          return {
            ...q,
            options: q.options.filter((_, oi) => oi !== optionIndex),
            correctOption:
              q.correctOption >= optionIndex && q.correctOption > 0
                ? q.correctOption - 1
                : q.correctOption,
          };
        }
        return q;
      }),
    }));
  };

  const addQuestionTag = (questionIndex, tag) => {
    if (!tag.trim()) return;
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex ? { ...q, tags: [...q.tags, tag.trim()] } : q
      ),
    }));
  };

  const removeQuestionTag = (questionIndex, tagIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex
          ? { ...q, tags: q.tags.filter((_, ti) => ti !== tagIndex) }
          : q
      ),
    }));
  };

  const handleNewAttachmentsChange = (e) => {
    setNewAttachments(Array.from(e.target.files));
  };

  const removeExistingAttachment = (attachmentId) => {
    setAttachmentsToRemove((prev) => [...prev, attachmentId]);
    setExistingAttachments((prev) =>
      prev.filter((att) => att._id !== attachmentId)
    );
  };

  const removeNewAttachment = (index) => {
    setNewAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const addRubricCriteria = () => {
    setFormData((prev) => ({
      ...prev,
      rubric: [...prev.rubric, { criteria: "", points: 0, description: "" }],
    }));
  };

  const removeRubricCriteria = (index) => {
    setFormData((prev) => ({
      ...prev,
      rubric: prev.rubric.filter((_, i) => i !== index),
    }));
  };

  const updateRubricCriteria = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rubric: prev.rubric.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: field === "points" ? parseInt(value) || 0 : value,
            }
          : item
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);

    // Validate due date
    if (!formData.dueDate) {
      toast.error("Please select a due date");
      setUpdateLoading(false);
      return;
    }

    const dueDate = new Date(formData.dueDate);
    if (isNaN(dueDate.getTime())) {
      toast.error("Please select a valid date for the due date");
      setUpdateLoading(false);
      return;
    }

    try {
      const isQuizOrExam = formData.type === "quiz" || formData.type === "exam";
      const hasFileUploads =
        !isQuizOrExam &&
        (newAttachments.length > 0 || attachmentsToRemove.length > 0);

      if (hasFileUploads) {
        // Use FormData for file uploads
        const formDataToSend = new FormData();

        // Add only updated fields
        formDataToSend.append("title", formData.title);
        formDataToSend.append("description", formData.description);
        formDataToSend.append("courseId", formData.courseId);
        formDataToSend.append("totalPoints", formData.totalPoints);
        formDataToSend.append("dueDate", dueDate.toISOString());
        formDataToSend.append("submissionType", formData.submissionType);
        formDataToSend.append("instructions", formData.instructions || "");
        formDataToSend.append("isPublished", formData.isPublished);
        formDataToSend.append(
          "allowLateSubmission",
          formData.allowLateSubmission
        );
        formDataToSend.append("latePenalty", formData.latePenalty);
        formDataToSend.append("maxFileSize", formData.maxFileSize);
        formDataToSend.append(
          "allowedFileTypes",
          JSON.stringify(formData.allowedFileTypes)
        );
        formDataToSend.append("rubric", JSON.stringify(formData.rubric));

        // Add attachments to remove
        if (attachmentsToRemove.length > 0) {
          formDataToSend.append(
            "attachmentsToRemove",
            JSON.stringify(attachmentsToRemove)
          );
        }

        // Add new attachment files
        newAttachments.forEach((file) => {
          formDataToSend.append("attachments", file);
        });

        await axios.put(`/api/assignments/${id}`, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Use JSON for quiz/exam or when no file uploads
        const payload = {
          title: formData.title,
          description: formData.description,
          courseId: formData.courseId,
          totalPoints: formData.totalPoints,
          dueDate: dueDate.toISOString(),
          instructions: formData.instructions || "",
          isPublished: formData.isPublished,
        };

        // Add quiz/exam specific fields
        if (isQuizOrExam) {
          payload.quizSettings = formData.quizSettings;
          payload.questions = formData.questions;
        } else {
          // Add non-quiz/exam specific fields
          payload.submissionType = formData.submissionType;
          payload.allowLateSubmission = formData.allowLateSubmission;
          payload.latePenalty = formData.latePenalty;
          payload.maxFileSize = formData.maxFileSize;
          payload.allowedFileTypes = formData.allowedFileTypes;
          payload.rubric = formData.rubric;
        }

        await axios.put(`/api/assignments/${id}`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });
      }

      toast.success("Assignment updated successfully!");
      navigate(`/assignments/${id}`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update assignment"
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  const fileTypes = [
    { value: "pdf", label: "PDF" },
    { value: "doc", label: "DOC" },
    { value: "docx", label: "DOCX" },
    { value: "txt", label: "TXT" },
    { value: "jpg", label: "JPG" },
    { value: "png", label: "PNG" },
    { value: "zip", label: "ZIP" },
  ];

  const getMinDateTime = () => {
    const minTime = new Date();
    minTime.setHours(minTime.getHours() + 1);
    return minTime.toISOString().slice(0, 16);
  };

  const isQuizOrExam = formData.type === "quiz" || formData.type === "exam";

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Assignment</h1>
        <p className="mt-2 text-gray-600">
          Update assignment details and settings
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignment Title *
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className="input"
                placeholder="Assignment 1: Data Structures"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course *
              </label>
              <select
                name="courseId"
                required
                value={formData.courseId}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.title} ({course.courseCode})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignment Type *
              </label>
              <select
                name="type"
                required
                value={formData.type}
                onChange={handleChange}
                className="input"
                disabled
              >
                <option value="homework">Homework</option>
                <option value="quiz">Quiz</option>
                <option value="exam">Exam</option>
                <option value="project">Project</option>
                <option value="presentation">Presentation</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Assignment type cannot be changed after creation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Points *
              </label>
              <input
                type="number"
                name="totalPoints"
                required
                min="1"
                value={formData.totalPoints}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date *
              </label>
              <input
                type="datetime-local"
                name="dueDate"
                required
                value={formData.dueDate}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                required
                rows={4}
                value={formData.description}
                onChange={handleChange}
                className="input"
                placeholder="Describe the assignment objectives and requirements..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions
              </label>
              <textarea
                name="instructions"
                rows={4}
                value={formData.instructions}
                onChange={handleChange}
                className="input"
                placeholder="Detailed instructions for completing the assignment..."
              />
            </div>
          </div>
        </div>

        {/* Attachments - Only for non-quiz/exam assignments */}
        {!isQuizOrExam && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Attachments
            </h2>

            {/* Existing Attachments */}
            {existingAttachments.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Attachments
                </label>
                <div className="space-y-2">
                  {existingAttachments.map((attachment) => (
                    <div
                      key={attachment._id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {attachment.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(attachment.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Download
                        </a>
                        <button
                          type="button"
                          onClick={() =>
                            removeExistingAttachment(attachment._id)
                          }
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add New Attachments
              </label>
              <input
                type="file"
                multiple
                onChange={handleNewAttachmentsChange}
                className="input"
              />
              {newAttachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {newAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex items-center space-x-3">
                        <svg
                          className="h-5 w-5 text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeNewAttachment(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quiz/Exam Settings */}
        {isQuizOrExam && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Quiz/Exam Settings
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Limit (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.quizSettings.timeLimit}
                  onChange={(e) =>
                    handleQuizSettingChange(
                      "timeLimit",
                      parseInt(e.target.value)
                    )
                  }
                  className="input"
                  placeholder="0 for no limit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Attempts
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.quizSettings.maxAttempts}
                  onChange={(e) =>
                    handleQuizSettingChange(
                      "maxAttempts",
                      parseInt(e.target.value)
                    )
                  }
                  className="input"
                />
              </div>

              <div className="md:col-span-2 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.quizSettings.randomizeQuestions}
                    onChange={(e) =>
                      handleQuizSettingChange(
                        "randomizeQuestions",
                        e.target.checked
                      )
                    }
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="ml-2 text-sm">Randomize Question Order</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.quizSettings.autoGrade}
                    onChange={(e) =>
                      handleQuizSettingChange("autoGrade", e.target.checked)
                    }
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="ml-2 text-sm">
                    Auto-Grade Multiple Choice Questions
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Questions */}
        {isQuizOrExam && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
              <button
                type="button"
                onClick={() => setShowBulkUpload(true)}
                className="btn btn-secondary btn-sm flex items-center"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                Bulk Upload
              </button>
            </div>

            {/* Question Type Selector */}
            <div className="flex space-x-2 mb-4">
              <button
                type="button"
                onClick={() => handleQuestionTypeChange("multiple-choice")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeQuestionType === "multiple-choice"
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Multiple Choice
              </button>
              <button
                type="button"
                onClick={() => handleQuestionTypeChange("written")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeQuestionType === "written"
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Written
              </button>
            </div>

            {/* Filtered Questions */}
            {formData.questions.filter((q) => q.type === activeQuestionType)
              .length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No{" "}
                {activeQuestionType === "multiple-choice"
                  ? "multiple choice"
                  : "written"}{" "}
                questions added yet.
              </p>
            ) : (
              <div className="space-y-4 mb-4">
                {formData.questions.map((question, qIndex) => {
                  if (question.type !== activeQuestionType) return null;

                  return (
                    <div
                      key={qIndex}
                      className="border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Question{" "}
                          {
                            formData.questions.filter(
                              (q, i) =>
                                q.type === activeQuestionType && i <= qIndex
                            ).length
                          }
                        </span>
                        <button
                          type="button"
                          onClick={() => removeQuestion(qIndex)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Question Text *
                          </label>
                          <textarea
                            value={question.questionText}
                            onChange={(e) =>
                              updateQuestion(
                                qIndex,
                                "questionText",
                                e.target.value
                              )
                            }
                            className="input"
                            rows="2"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Points
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={question.points}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "points",
                                  parseInt(e.target.value)
                                )
                              }
                              className="input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Difficulty
                            </label>
                            <select
                              value={question.difficulty}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "difficulty",
                                  e.target.value
                                )
                              }
                              className="input"
                            >
                              <option value="easy">Easy</option>
                              <option value="medium">Medium</option>
                              <option value="hard">Hard</option>
                            </select>
                          </div>
                        </div>

                        {question.type === "multiple-choice" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Options
                            </label>
                            <div className="space-y-2">
                              {question.options.map((option, oIndex) => (
                                <div
                                  key={oIndex}
                                  className="flex items-center space-x-2"
                                >
                                  <input
                                    type="radio"
                                    name={`question-${qIndex}-correct`}
                                    checked={question.correctOption === oIndex}
                                    onChange={() =>
                                      updateQuestion(
                                        qIndex,
                                        "correctOption",
                                        oIndex
                                      )
                                    }
                                    className="text-primary-600"
                                  />
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) =>
                                      updateQuestionOption(
                                        qIndex,
                                        oIndex,
                                        e.target.value
                                      )
                                    }
                                    className="input flex-1"
                                    placeholder={`Option ${oIndex + 1}`}
                                  />
                                  {question.options.length > 2 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeQuestionOption(qIndex, oIndex)
                                      }
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() => addQuestionOption(qIndex)}
                              className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                            >
                              + Add Option
                            </button>
                          </div>
                        )}

                        {question.type === "written" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Expected Answer (Optional)
                            </label>
                            <textarea
                              value={question.expectedAnswer}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "expectedAnswer",
                                  e.target.value
                                )
                              }
                              className="input"
                              rows="3"
                              placeholder="Model answer or key points..."
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Explanation (Optional)
                          </label>
                          <textarea
                            value={question.explanation}
                            onChange={(e) =>
                              updateQuestion(
                                qIndex,
                                "explanation",
                                e.target.value
                              )
                            }
                            className="input"
                            rows="2"
                            placeholder="Explanation or feedback..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tags (Optional)
                          </label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {question.tags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                              >
                                {tag}
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeQuestionTag(qIndex, tagIndex)
                                  }
                                  className="ml-1.5 inline-flex items-center justify-center text-primary-600 hover:text-primary-800"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Add a tag and press Enter"
                              className="input flex-1"
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addQuestionTag(qIndex, e.target.value);
                                  e.target.value = "";
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Question Button */}
            <button
              type="button"
              onClick={() => addQuestion(activeQuestionType)}
              className="btn btn-secondary w-full flex items-center justify-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add{" "}
              {activeQuestionType === "multiple-choice"
                ? "Multiple Choice"
                : "Written"}{" "}
              Question
            </button>
          </div>
        )}

        {/* Submission Settings */}
        {!isQuizOrExam && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Submission Settings
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Submission Type
                </label>
                <select
                  name="submissionType"
                  value={formData.submissionType}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="file">File Upload Only</option>
                  <option value="text">Text Submission Only</option>
                  <option value="both">Both File and Text</option>
                </select>
              </div>

              {formData.submissionType !== "text" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allowed File Types
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {fileTypes.map((type) => (
                        <label key={type.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.allowedFileTypes.includes(
                              type.value
                            )}
                            onChange={(e) =>
                              handleFileTypeChange(type.value, e.target.checked)
                            }
                            className="rounded border-gray-300 text-primary-600"
                          />
                          <span className="ml-2 text-sm">{type.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max File Size (MB)
                    </label>
                    <input
                      type="number"
                      name="maxFileSize"
                      min="1"
                      max="100"
                      value={Math.round(formData.maxFileSize / 1024 / 1024)}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          maxFileSize: parseInt(e.target.value) * 1024 * 1024,
                        }))
                      }
                      className="input"
                    />
                  </div>
                </>
              )}

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="allowLateSubmission"
                    checked={formData.allowLateSubmission}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="ml-2 text-sm">Allow Late Submissions</span>
                </label>

                {formData.allowLateSubmission && (
                  <div className="flex items-center space-x-2">
                    <label className="text-sm">Penalty per day:</label>
                    <input
                      type="number"
                      name="latePenalty"
                      min="0"
                      max="100"
                      value={formData.latePenalty}
                      onChange={handleChange}
                      className="input w-20"
                    />
                    <span className="text-sm">%</span>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="ml-2 text-sm">Published</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Rubric */}
        {!isQuizOrExam && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Grading Rubric
              </h2>
              <button
                type="button"
                onClick={addRubricCriteria}
                className="btn btn-secondary btn-sm flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Criteria
              </button>
            </div>

            {formData.rubric.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No rubric criteria added. Click "Add Criteria" to get started.
              </p>
            ) : (
              <div className="space-y-4">
                {formData.rubric.map((criteria, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border rounded-lg"
                  >
                    <div className="md:col-span-4">
                      <input
                        type="text"
                        placeholder="Criteria name"
                        value={criteria.criteria}
                        onChange={(e) =>
                          updateRubricCriteria(
                            index,
                            "criteria",
                            e.target.value
                          )
                        }
                        className="input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <input
                        type="number"
                        placeholder="Points"
                        value={criteria.points}
                        onChange={(e) =>
                          updateRubricCriteria(index, "points", e.target.value)
                        }
                        className="input"
                      />
                    </div>
                    <div className="md:col-span-5">
                      <input
                        type="text"
                        placeholder="Description"
                        value={criteria.description}
                        onChange={(e) =>
                          updateRubricCriteria(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        className="input"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <button
                        type="button"
                        onClick={() => removeRubricCriteria(index)}
                        className="btn btn-danger btn-sm w-full"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/assignments/${id}`)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateLoading}
            className="btn btn-primary disabled:opacity-50"
          >
            {updateLoading ? "Updating..." : "Update Assignment"}
          </button>
        </div>
      </form>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Upload Questions
              </h3>
              <button
                type="button"
                onClick={() => setShowBulkUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Excel Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                  <TableCellsIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <h4 className="font-medium text-gray-900 mb-2">
                    Upload Excel
                  </h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload questions from an Excel file
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      toast("Excel upload functionality coming soon!", {
                        icon: (
                          <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                        ),
                      });
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    Choose File
                  </button>
                </div>

                {/* JSON Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                  <CodeBracketIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <h4 className="font-medium text-gray-900 mb-2">
                    Upload JSON
                  </h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload questions from a JSON file
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      toast("JSON upload functionality coming soon!", {
                        icon: (
                          <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                        ),
                      });
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    Choose File
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Format Requirements:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Excel: First row should contain headers</li>
                      <li>JSON: Array of question objects</li>
                      <li>Required fields: questionText, type, points</li>
                      <li>
                        MCQ: Include options array and correctOption index
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentEdit;
